// Verification & agreement data layer (spec §5.8/§11): the accepted Host
// Agreement text (rendered from the DB, never `frontend/src/lib/hostTerms.ts`
// — that file is mobile's canonical copy and must stay byte-identical to
// main; this screen shows what THIS owner actually accepted, which is
// `business_agreements.terms_text`, 20260914000002), KYC document status
// (`business_kyc`), and any pending negotiated commission-rate proposal
// (`commission_rate_agreements`, 20260914000001) — mirrors
// `frontend/app/business/commission-rate.tsx`'s accept flow.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { sha256Hex } from '../webSha256';
import { writableTable } from './supabaseWrite';
import { cancelThenInvalidate } from '@/lib/query/refresh';

export interface AgreementRow {
  id: string;
  business_id: string | null;
  version: string;
  accepted_at: string;
  terms_text: string | null;
  terms_sha256: string | null;
}

export function useCurrentAgreement(supabase: SupabaseClient, userId: string | null, businessId: string | null) {
  return useQuery({
    queryKey: ['agreement', userId, businessId],
    queryFn: async (): Promise<AgreementRow | null> => {
      const { data, error } = await supabase
        .from('business_agreements')
        .select('id, business_id, version, accepted_at, terms_text, terms_sha256')
        .eq('owner_id', userId as string)
        .order('accepted_at', { ascending: false })
        .limit(1);
      if (error) throw new Error(error.message);
      const rows = (data ?? []) as AgreementRow[];
      return rows[0] ?? null;
    },
    enabled: !!userId,
  });
}

export type KycDocType =
  | 'government_id'
  | 'business_registration'
  | 'proof_of_address'
  | 'bank_confirmation'
  | 'ntb_certificate'
  | 'fitness_certificate';
export type KycStatus = 'pending' | 'approved' | 'rejected';

export interface KycRow {
  id: string;
  doc_type: KycDocType;
  status: KycStatus;
  updated_at: string;
}

export const KYC_DOC_LABELS: Record<KycDocType, string> = {
  government_id: 'Government ID',
  business_registration: 'Business registration',
  proof_of_address: 'Proof of address',
  bank_confirmation: 'Bank confirmation',
  ntb_certificate: 'NTB registration certificate',
  fitness_certificate: 'Certificate of fitness',
};

/** TASKS 111: mirrors the app's `kycDocTypesFor` (frontend/src/lib/data/kyc.ts)
 * — four base documents plus the per-category credential. The old fixed list
 * showed Smile ID's "identity liveness" (removed 09-01) and never the NTB
 * certificate the app actually asks a lodge for. */
export function kycDocTypesFor(category: string | null | undefined): KycDocType[] {
  const docs: KycDocType[] = ['government_id', 'business_registration', 'proof_of_address', 'bank_confirmation'];
  if (category === 'accommodation' || category === 'tour') docs.push('ntb_certificate');
  else if (category === 'restaurant') docs.push('fitness_certificate');
  return docs;
}

export function useBusinessCategory(supabase: SupabaseClient, businessId: string | null) {
  return useQuery({
    queryKey: ['business-category', businessId],
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await supabase.from('businesses').select('category').eq('id', businessId as string).maybeSingle();
      if (error) throw new Error(error.message);
      return (data as { category: string | null } | null)?.category ?? null;
    },
    enabled: !!businessId,
  });
}

export function useKycStatus(supabase: SupabaseClient, userId: string | null) {
  return useQuery({
    queryKey: ['kyc', userId],
    queryFn: async (): Promise<KycRow[]> => {
      const { data, error } = await supabase
        .from('business_kyc')
        .select('id, doc_type, status, updated_at')
        .eq('owner_id', userId as string);
      if (error) throw new Error(error.message);
      return (data ?? []) as KycRow[];
    },
    enabled: !!userId,
  });
}

export type RateAgreementStatus = 'proposed' | 'accepted' | 'withdrawn' | 'superseded';

export interface CommissionRateAgreement {
  id: string;
  business_id: string;
  owner_id: string;
  rate: number;
  rate_text: string;
  rate_text_sha256: string | null;
  status: RateAgreementStatus;
  accepted_at: string | null;
}

export function useCurrentRateAgreement(supabase: SupabaseClient, businessId: string | null) {
  return useQuery({
    queryKey: ['commission-rate-agreement', businessId],
    queryFn: async (): Promise<CommissionRateAgreement | null> => {
      const { data, error } = await supabase
        .from('commission_rate_agreements')
        .select('id, business_id, owner_id, rate, rate_text, rate_text_sha256, status, accepted_at')
        .eq('business_id', businessId as string)
        .in('status', ['proposed', 'accepted'])
        .order('proposed_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return (data as CommissionRateAgreement | null) ?? null;
    },
    enabled: !!businessId,
  });
}

export function useAcceptRateProposal(supabase: SupabaseClient, businessId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (agreement: CommissionRateAgreement): Promise<CommissionRateAgreement> => {
      const fingerprint = await sha256Hex(agreement.rate_text);
      const { data, error } = await writableTable(supabase, 'commission_rate_agreements')
        .update({ status: 'accepted', rate_text_sha256: fingerprint, platform: 'web' })
        .eq('id', agreement.id)
        .select('id, business_id, owner_id, rate, rate_text, rate_text_sha256, status, accepted_at')
        .single();
      if (error) throw new Error(error.message);
      return data as CommissionRateAgreement;
    },
    onSuccess: () => {
      cancelThenInvalidate(queryClient, { queryKey: ['commission-rate-agreement', businessId] });
    },
  });
}

export function formatRatePct(rate: number): string {
  const pct = rate * 100;
  return `${Number.isInteger(pct) ? pct : Number(pct.toFixed(2))}%`;
}
